import React, { useCallback, useEffect, useState } from "react";
import { ConnectModal, useSignAndExecuteTransaction, useSuiClient, useSignPersonalMessage } from "@mysten/dapp-kit";
import { ShieldCheck, Loader2, Lock, Unlock, Wallet, Target, AlertTriangle, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { api, shortAddr } from "@/lib/api";
import { adminSetPayout, adminSetPaused, adminSetCurveParams } from "@/lib/sui";
import { getWalletProof } from "@/lib/walletProof";

export default function Admin() {
  const { connected, address } = useWallet();
  const client = useSuiClient();
  const { mutateAsync: signExec } = useSignAndExecuteTransaction();
  const { mutateAsync: signPersonalMessage } = useSignPersonalMessage();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [platform, setPlatform] = useState("");
  const [migrator, setMigrator] = useState("");
  const [gradSui, setGradSui] = useState("");
  const [coins, setCoins] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/config");
      setCfg(data);
      if (data) {
        setPlatform(data.platform || "");
        setMigrator(data.migrator || "");
        setGradSui(String((data.graduation_sui || 0) / 1e9));
      }
    } catch (e) {} finally { setLoading(false); }
  }, []);

  const loadCoins = useCallback(async () => {
    try { const { data } = await api.get("/coins/onchain"); setCoins(data || []); } catch (e) {}
  }, []);

  useEffect(() => { load(); loadCoins(); }, [load, loadCoins]);

  const isAdmin = connected && cfg && address && address === cfg.admin_cap_owner;
  const common = cfg ? { pkg: cfg.package_id, adminCapId: cfg.admin_cap_id, configId: cfg.config_id } : {};

  const run = async (label, fn) => {
    setBusy(label);
    try {
      const digest = await fn();
      toast.success(`Done · ${digest.slice(0, 8)}…`);
      await load();
    } catch (e) {
      toast.error((e?.message || "Transaction failed").slice(0, 140));
    } finally { setBusy(""); }
  };

  const removeCoins = async (payload, label) => {
    setBusy(label);
    try {
      const proof = await getWalletProof(signPersonalMessage, address, "admin_remove");
      const { data } = await api.post("/admin/coins/remove", { address, ...proof, ...payload });
      toast.success(`Removed ${data.deleted} coin${data.deleted === 1 ? "" : "s"} from the board`);
      await loadCoins();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (e?.message || "Remove failed").slice(0, 140));
    } finally { setBusy(""); }
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading…</div>;
  if (!cfg) return <div className="max-w-2xl mx-auto px-4 py-16 text-center text-muted-foreground">No launchpad deployed.</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-6 h-6 text-[#1EC62E]" />
        <h1 className="font-display font-extrabold text-3xl">Admin <span className="gradient-text">Console</span></h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Manage the launchpad Config on-chain. Every change is signed by the <b>AdminCap</b> holder in their own wallet.</p>

      {/* status */}
      <div className="card-surface p-5 mb-5 text-sm font-mono space-y-2">
        <Row label="AdminCap owner" val={shortAddr(cfg.admin_cap_owner)} />
        <Row label="Config" val={shortAddr(cfg.config_id)} />
        <Row label="Platform fee wallet" val={shortAddr(cfg.platform)} />
        <Row label="Migrator wallet" val={shortAddr(cfg.migrator)} />
        <Row label="Total fee" val={`${cfg.fee_bps / 100}% (creator ${cfg.creator_fee_bps / 100}% of fee)`} />
        <Row label="Graduation" val={`${(cfg.graduation_sui / 1e9).toLocaleString()} SUI`} />
        <div className="flex justify-between py-1.5"><span className="text-muted-foreground">Trading</span><span className={cfg.paused ? "text-[#F43F5E]" : "text-[#1EC62E]"}>{cfg.paused ? "PAUSED" : "LIVE"}</span></div>
      </div>

      {!connected ? (
        <ConnectModal trigger={<button className="w-full py-3 rounded-lg font-display font-bold text-black" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>Connect the admin wallet</button>} />
      ) : !isAdmin ? (
        <div className="card-surface p-5 flex gap-2 items-start text-sm text-[#FFB800] border border-[#FFB800]/30 bg-[#FFB800]/5">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Connected wallet <b>{shortAddr(address)}</b> is not the AdminCap holder ({shortAddr(cfg.admin_cap_owner)}). Switch to the admin wallet to make changes.</span>
        </div>
      ) : (
        <div className="space-y-5" data-testid="admin-controls">
          {/* automatic migration */}
          <div className="card-surface p-5 border border-[#00E5FF]/30" style={{ background: "linear-gradient(90deg,rgba(0,229,255,0.05),rgba(255,0,122,0.04))" }}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 font-display font-bold"><Sparkles className="w-4 h-4 text-[#00E5FF]" /> Automatic migration</div>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${cfg.auto_migration_enabled ? "text-[#1EC62E] bg-[#1EC62E]/10 border border-[#1EC62E]/30" : "text-[#FFB800] bg-[#FFB800]/10 border border-[#FFB800]/30"}`}>{cfg.auto_migration_enabled ? "ENABLED" : "OFF"}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Turn this on once and every coin that hits its threshold graduates to Cetus automatically — no clicks, even if the dev is offline. It seeds the pool with all remaining tokens + collected SUI, permanently locks the LP (rug-proof), and gives the fee-claim proof to each coin's creator (dev claims pool fees). It sets the keeper as the migrator so it can seed the pool.</p>
            <div className="text-xs font-mono space-y-1.5 mb-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Keeper wallet</span><span className="text-[#1EC62E]">{shortAddr(cfg.keeper_address)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Keeper gas balance</span><span className={cfg.keeper_balance != null && cfg.keeper_balance < 0.3 ? "text-[#F43F5E]" : ""}>{cfg.keeper_balance != null ? cfg.keeper_balance.toFixed(3) : "—"} SUI</span></div>
            </div>
            {cfg.keeper_balance != null && cfg.keeper_balance < 0.3 && (
              <div className="flex gap-2 items-start text-[11px] text-[#FFB800] bg-[#FFB800]/10 rounded-lg p-2 mb-3 border border-[#FFB800]/25"><AlertTriangle className="w-4 h-4 shrink-0" /> Low gas — send some SUI to the keeper wallet so it can pay for migrations.</div>
            )}
            {cfg.auto_migration_enabled ? (
              <div className="w-full py-2.5 rounded-lg text-center font-bold text-sm text-[#1EC62E] bg-[#1EC62E]/10 border border-[#1EC62E]/30" data-testid="auto-migration-status">✓ Auto-migration is live</div>
            ) : (
              <button
                data-testid="enable-auto-migration"
                disabled={!!busy || !cfg.keeper_address}
                onClick={() => run("auto", () => adminSetPayout(signExec, client, { ...common, platform: cfg.platform, migrator: cfg.keeper_address }))}
                className="w-full py-3 rounded-lg font-display font-bold text-black disabled:opacity-50" style={{ background: "linear-gradient(90deg,#00E5FF,#FF007A)" }}>
                {busy === "auto" ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Enable automatic migration"}
              </button>
            )}
          </div>

          {/* pause */}
          <div className="card-surface p-5">
            <div className="flex items-center gap-2 mb-1 font-display font-bold">{cfg.paused ? <Lock className="w-4 h-4 text-[#F43F5E]" /> : <Unlock className="w-4 h-4 text-[#1EC62E]" />} Trading status</div>
            <p className="text-xs text-muted-foreground mb-3">Emergency pause blocks all buys and sells across every coin.</p>
            <button
              data-testid="admin-toggle-pause"
              disabled={!!busy}
              onClick={() => run("pause", () => adminSetPaused(signExec, client, { ...common, paused: !cfg.paused }))}
              className="w-full py-2.5 rounded-lg font-bold text-sm disabled:opacity-50"
              style={cfg.paused ? { background: "linear-gradient(90deg,#1EC62E,#7CF24E)", color: "#000" } : { background: "linear-gradient(90deg,#F43F5E,#FF007A)", color: "#fff" }}>
              {busy === "pause" ? <Loader2 className="w-4 h-4 animate-spin inline" /> : cfg.paused ? "Resume trading" : "Pause all trading"}
            </button>
          </div>

          {/* payout wallets */}
          <div className="card-surface p-5">
            <div className="flex items-center gap-2 mb-3 font-display font-bold"><Wallet className="w-4 h-4 text-[#1EC62E]" /> Payout wallets</div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Platform fee wallet</label>
            <input data-testid="admin-platform-input" value={platform} onChange={(e) => setPlatform(e.target.value)} placeholder="0x…" className="w-full mt-1 mb-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#1EC62E]/50" />
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Migrator wallet</label>
            <input data-testid="admin-migrator-input" value={migrator} onChange={(e) => setMigrator(e.target.value)} placeholder="0x…" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#1EC62E]/50" />
            <button
              data-testid="admin-save-payout"
              disabled={!!busy || !platform.startsWith("0x") || !migrator.startsWith("0x")}
              onClick={() => run("payout", () => adminSetPayout(signExec, client, { ...common, platform: platform.trim(), migrator: migrator.trim() }))}
              className="w-full mt-3 py-2.5 rounded-lg font-bold text-sm text-black disabled:opacity-50" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>
              {busy === "payout" ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Update payout wallets"}
            </button>
          </div>

          {/* graduation threshold */}
          <div className="card-surface p-5">
            <div className="flex items-center gap-2 mb-3 font-display font-bold"><Target className="w-4 h-4 text-[#FFB800]" /> Graduation threshold</div>
            <p className="text-xs text-muted-foreground mb-3">SUI a curve must collect before it graduates to Cetus. The virtual reserve is auto-set to half this (the curve raises exactly this amount when its 750M tokens sell out). Applies to newly created curves. Recommended: 7,500.</p>
            <div className="flex gap-2">
              <input data-testid="admin-grad-input" type="number" min="1" value={gradSui} onChange={(e) => setGradSui(e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#1EC62E]/50" />
              <span className="flex items-center px-3 text-sm text-muted-foreground font-mono">SUI</span>
            </div>
            <button
              data-testid="admin-save-grad"
              disabled={!!busy || !(Number(gradSui) > 0)}
              onClick={() => run("grad", () => adminSetCurveParams(signExec, client, { ...common, virtualSui: BigInt(Math.floor(Number(gradSui) * 1e9 / 2)), graduationSui: BigInt(Math.floor(Number(gradSui) * 1e9)) }))}
              className="w-full mt-3 py-2.5 rounded-lg font-bold text-sm text-black disabled:opacity-50" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>
              {busy === "grad" ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Update threshold"}
            </button>
          </div>

          {/* manage coins */}
          <div className="card-surface p-5 border border-[#F43F5E]/30" style={{ background: "linear-gradient(90deg,rgba(255,59,48,0.05),transparent)" }} data-testid="admin-manage-coins">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 font-display font-bold"><Trash2 className="w-4 h-4 text-[#F43F5E]" /> Manage coins</div>
              <span className="text-xs text-muted-foreground font-mono">{coins.length} listed</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Remove a coin's listing from the board (stats, trades, board). The coin still exists on-chain — this only hides it from the site. Use it to clear test launches.</p>
            {coins.length > 0 && (
              <button
                data-testid="admin-remove-all"
                disabled={!!busy}
                onClick={() => { if (window.confirm(`Remove ALL ${coins.length} coins from the board? They stay on-chain but disappear from the site.`)) removeCoins({ all: true }, "remove-all"); }}
                className="w-full mb-3 py-2.5 rounded-lg font-bold text-sm text-white disabled:opacity-50" style={{ background: "linear-gradient(90deg,#F43F5E,#FF007A)" }}>
                {busy === "remove-all" ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `Remove all ${coins.length} coins`}
              </button>
            )}
            <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="admin-coin-list">
              {coins.map((c) => (
                <div key={c.curve_id} data-testid={`admin-coin-${c.symbol}`} className="flex items-center gap-2 text-sm border-t border-white/5 py-2">
                  <span className="font-mono text-[#1EC62E] font-bold w-24 truncate">${c.symbol}</span>
                  <span className="text-muted-foreground truncate flex-1">{c.name}</span>
                  <button
                    data-testid={`admin-remove-${c.symbol}`}
                    disabled={!!busy}
                    onClick={() => removeCoins({ curve_ids: [c.curve_id] }, `remove-${c.curve_id}`)}
                    className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold text-[#F43F5E] border border-[#F43F5E]/40 hover:bg-[#F43F5E]/10 disabled:opacity-40">
                    {busy === `remove-${c.curve_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Trash2 className="w-3.5 h-3.5" /> Remove</>}
                  </button>
                </div>
              ))}
              {coins.length === 0 && <div className="py-4 text-center text-muted-foreground text-sm">No coins on the board.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, val }) {
  return <div className="flex justify-between border-b border-white/5 py-1.5 gap-2"><span className="text-muted-foreground shrink-0">{label}</span><span className="truncate text-[#1EC62E]">{val}</span></div>;
}
